<script lang="ts">
  import { onMount } from 'svelte';
  import Prose from '../lib/components/Prose.svelte';

  type Trial = { passed: boolean; elapsed_s: number };
  type Cell = {
    idx: number;
    label: string;
    n: number;
    k: number;
    passRate: number;
    ciLow: number;
    ciHigh: number;
    trials: Trial[];
  };
  type Data = {
    model: string;
    targetLow: number;
    targetHigh: number;
    calibratedCell: number;
    cells: Cell[];
  };

  let data = $state<Data | null>(null);

  onMount(async () => {
    const res = await fetch('./calibrate-cells.json');
    data = await res.json();
  });

  // Chart geometry
  const W = 720;
  const H = 380;
  const M = { top: 36, right: 40, bottom: 78, left: 64 };
  const PLOT_W = W - M.left - M.right;
  const PLOT_H = H - M.top - M.bottom;

  function cellX(i: number, n: number): number {
    return M.left + ((i + 0.5) / n) * PLOT_W;
  }
  function yRate(p: number): number {
    return M.top + (1 - p) * PLOT_H;
  }

  // "6x7//5" → "6d × 7d ÷ 5d". The raw labels are Python-style
  // floor-division notation; show the operand sizes in plain math instead.
  function prettyLabel(raw: string): string {
    const m = raw.match(/^(\d+)x(\d+)\/\/(\d+)$/);
    if (!m) return raw;
    return `${m[1]}d × ${m[2]}d ÷ ${m[3]}d`;
  }

  let trendPath = $derived.by(() => {
    if (!data) return '';
    const n = data.cells.length;
    return data.cells.map((c, i) => `${cellX(i, n)},${yRate(c.passRate)}`).join(' ');
  });

  // ── Expanded 7x7//5 chart ──────────────────────────────────────────
  // Running pass-rate with a narrowing 95% Wilson CI band: the headline
  // "consistency" claim is that at the calibrated difficulty, the rate
  // settles as n grows.
  const W2 = 720;
  const H2 = 300;
  const M2 = { top: 36, right: 40, bottom: 64, left: 64 };
  const PLOT_W2 = W2 - M2.left - M2.right;
  const PLOT_H2 = H2 - M2.top - M2.bottom;

  function wilson(k: number, n: number, z = 1.96): { low: number; high: number } {
    if (n === 0) return { low: 0, high: 1 };
    const p = k / n;
    const denom = 1 + (z * z) / n;
    const center = (p + (z * z) / (2 * n)) / denom;
    const half = (z * Math.sqrt((p * (1 - p)) / n + (z * z) / (4 * n * n))) / denom;
    return { low: Math.max(0, center - half), high: Math.min(1, center + half) };
  }

  type RunPoint = { i: number; passed: boolean; rate: number; low: number; high: number };

  function runningSeries(trials: Trial[]): RunPoint[] {
    let k = 0;
    return trials.map((t, j) => {
      if (t.passed) k += 1;
      const n = j + 1;
      const { low, high } = wilson(k, n);
      return { i: n, passed: t.passed, rate: k / n, low, high };
    });
  }

  function x2(i: number, n: number): number {
    if (n <= 1) return M2.left + PLOT_W2 / 2;
    return M2.left + ((i - 1) / (n - 1)) * PLOT_W2;
  }
  function y2(p: number): number {
    return M2.top + (1 - p) * PLOT_H2;
  }

  let expanded = $derived(data ? data.cells.find((c) => c.idx === data!.calibratedCell) ?? null : null);
  let series = $derived(expanded ? runningSeries(expanded.trials) : []);
  let bandPath = $derived.by(() => {
    if (series.length === 0) return '';
    const n = series.length;
    const top = series.map((s) => `${x2(s.i, n)},${y2(s.high)}`);
    const bottom = series.map((s) => `${x2(s.i, n)},${y2(s.low)}`).reverse();
    return `M${top.join(' L')} L${bottom.join(' L')} Z`;
  });
  let linePath = $derived.by(() => {
    if (series.length === 0) return '';
    const n = series.length;
    return 'M' + series.map((s) => `${x2(s.i, n)},${y2(s.rate)}`).join(' L');
  });
</script>

<section id="consistency" class="section">
  <Prose>
    <header class="header">
      <div class="eyebrow">Measurement</div>
      <h2>Pass rates hold still</h2>
      <p class="summary">
        Individual answers vary. Aggregate pass rates settle, and they move
        predictably as the task gets harder.
      </p>
    </header>
    <p>
      A model does not answer the same prompt the same way every time. Ask Haiku
      to multiply two numbers and one run may pass while the next fails. That
      raises the basic measurement question: is pass rate a real signal, or only
      randomness with a percentage sign attached?
    </p>
    <p>
      The task here is <em>floor((A × B) / C)</em>. Difficulty is controlled by
      the digit counts of A, B, and C.
    </p>
  </Prose>

  {#if data}
    <div class="chart-wrap">
      <svg viewBox="0 0 {W} {H}" role="img" aria-label="Haiku pass rate by arithmetic difficulty with confidence intervals">
        <!-- Y grid + labels -->
        {#each [0, 0.25, 0.5, 0.75, 1] as t}
          <line
            x1={M.left} x2={M.left + PLOT_W}
            y1={yRate(t)} y2={yRate(t)}
            stroke="var(--border)" stroke-width="1" stroke-dasharray="2 3"
          />
          <text
            x={M.left - 8} y={yRate(t) + 3.5}
            text-anchor="end"
            class="axis-label"
          >{(t * 100).toFixed(0)}%</text>
        {/each}

        <!-- Sweet-spot band: informative difficulties, not guaranteed wins or losses -->
        <rect
          x={M.left} y={yRate(data.targetHigh)}
          width={PLOT_W} height={yRate(data.targetLow) - yRate(data.targetHigh)}
          fill="var(--viz-accent)" fill-opacity="0.04"
        />
        <text
          x={M.left + PLOT_W - 6}
          y={yRate(data.targetHigh) + 13}
          text-anchor="end"
          class="target-label"
        >useful measurement band {(data.targetLow * 100).toFixed(0)}–{(data.targetHigh * 100).toFixed(0)}%</text>

        <!-- Axis lines -->
        <line
          x1={M.left} x2={M.left + PLOT_W}
          y1={M.top + PLOT_H} y2={M.top + PLOT_H}
          stroke="var(--ink-subtle)" stroke-width="1.2"
        />
        <line
          x1={M.left} x2={M.left}
          y1={M.top} y2={M.top + PLOT_H}
          stroke="var(--ink-subtle)" stroke-width="1.2"
        />

        <!-- Axis titles -->
        <text
          transform="translate(16, {M.top + PLOT_H / 2}) rotate(-90)"
          text-anchor="middle"
          class="axis-title"
        >pass rate</text>
        <text
          x={M.left + PLOT_W / 2}
          y={H - 8}
          text-anchor="middle"
          class="axis-title"
        >operand sizes (A digits × B digits ÷ C digits) — harder →</text>

        <!-- Trend line behind the points: shows the smooth descent -->
        <polyline
          points={trendPath}
          fill="none"
          stroke="var(--viz-accent)"
          stroke-width="1.5"
          stroke-dasharray="4 4"
          stroke-opacity="0.5"
        />

        {#each data.cells as c, i}
          {@const cx = cellX(i, data.cells.length)}
          {@const yPoint = yRate(c.passRate)}
          {@const yLow = yRate(c.ciLow)}
          {@const yHigh = yRate(c.ciHigh)}

          <!-- CI error bar -->
          <line
            x1={cx} x2={cx}
            y1={yHigh} y2={yLow}
            stroke="var(--ink-muted)" stroke-width="2"
          />
          <line
            x1={cx - 8} x2={cx + 8}
            y1={yHigh} y2={yHigh}
            stroke="var(--ink-muted)" stroke-width="2"
          />
          <line
            x1={cx - 8} x2={cx + 8}
            y1={yLow} y2={yLow}
            stroke="var(--ink-muted)" stroke-width="2"
          />

          <!-- Pass-rate point -->
          <circle
            cx={cx} cy={yPoint}
            r="7"
            fill={c.idx === data.calibratedCell ? 'var(--viz-accent)' : 'var(--bg)'}
            stroke="var(--viz-accent)"
            stroke-width="2"
          />

          <!-- Cell label (operand sizes in plain math) -->
          <text x={cx} y={M.top + PLOT_H + 32} text-anchor="middle" class="axis-label">
            {prettyLabel(c.label)}
          </text>
          <!-- trial count -->
          <text x={cx} y={M.top + PLOT_H + 48} text-anchor="middle" class="axis-label dim">
            {c.n} trial{c.n === 1 ? '' : 's'}
          </text>

          <!-- Pass rate value above point -->
          <text x={cx + 14} y={yPoint + 4} class="point-label">
            {(c.passRate * 100).toFixed(1)}%
          </text>
        {/each}
      </svg>
    </div>

    {#if expanded}
      <Prose>
        <p>
          Three difficulties produce three ordered rates. The easiest case passes
          every time; the hardest falls to 60%. There is no strange jump. As
          operands grow, arithmetic steps get less reliable, and the longer chain
          compounds the loss.
        </p>
        <p>
          The degradation is not mysterious. Single-digit facts such as <em>7 ×
          8 = 56</em> are common enough in training that the model can effectively
          look them up. Multi-digit multiplication is composed from those facts,
          carries, and place-value bookkeeping. Larger operands require more
          composition, so both the step count and the opportunity for errors rise.
        </p>
        <p>
          The middle cell is the informative one: neither a guaranteed pass nor a
          guaranteed fail. That is where repeated trials can measure something, so
          it got 45 runs.
        </p>
        <h3 class="subhead">45 repeated runs at {prettyLabel(expanded.label)}</h3>
      </Prose>

      <div class="chart-wrap">
        <svg viewBox="0 0 {W2} {H2}" role="img" aria-label="Running pass rate with narrowing 95% confidence interval across 45 trials at {prettyLabel(expanded.label)}">
          <!-- Y grid + labels -->
          {#each [0, 0.25, 0.5, 0.75, 1] as t}
            <line
              x1={M2.left} x2={M2.left + PLOT_W2}
              y1={y2(t)} y2={y2(t)}
              stroke="var(--border)" stroke-width="1" stroke-dasharray="2 3"
            />
            <text
              x={M2.left - 8} y={y2(t) + 3.5}
              text-anchor="end"
              class="axis-label"
            >{(t * 100).toFixed(0)}%</text>
          {/each}

          <!-- Target band -->
          <rect
            x={M2.left} y={y2(data.targetHigh)}
            width={PLOT_W2} height={y2(data.targetLow) - y2(data.targetHigh)}
            fill="var(--viz-accent)" fill-opacity="0.04"
          />

          <!-- Axis lines -->
          <line
            x1={M2.left} x2={M2.left + PLOT_W2}
            y1={M2.top + PLOT_H2} y2={M2.top + PLOT_H2}
            stroke="var(--ink-subtle)" stroke-width="1.2"
          />
          <line
            x1={M2.left} x2={M2.left}
            y1={M2.top} y2={M2.top + PLOT_H2}
            stroke="var(--ink-subtle)" stroke-width="1.2"
          />

          <!-- Axis titles -->
          <text
            transform="translate(16, {M2.top + PLOT_H2 / 2}) rotate(-90)"
            text-anchor="middle"
            class="axis-title"
          >running pass rate</text>
          <text
            x={M2.left + PLOT_W2 / 2}
            y={H2 - 8}
            text-anchor="middle"
            class="axis-title"
          >trial number (1 … {expanded.n}) at {prettyLabel(expanded.label)}</text>

          <!-- X tick labels at intervals -->
          {#each [1, 10, 20, 30, 40, expanded.n] as t (t)}
            <text
              x={x2(t, expanded.n)} y={M2.top + PLOT_H2 + 16}
              text-anchor="middle"
              class="axis-label dim"
            >{t}</text>
          {/each}

          <!-- CI band (narrows as n grows) -->
          <path d={bandPath} fill="var(--viz-accent)" fill-opacity="0.12" />

          <!-- Final-rate dashed reference -->
          <line
            x1={M2.left} x2={M2.left + PLOT_W2}
            y1={y2(expanded.passRate)} y2={y2(expanded.passRate)}
            stroke="var(--viz-accent)" stroke-width="1" stroke-dasharray="3 4" stroke-opacity="0.5"
          />

          <!-- Running pass-rate line -->
          <path d={linePath} fill="none" stroke="var(--viz-accent)" stroke-width="2" />

          <!-- Per-trial ticks along the bottom -->
          <!-- Width computed so the row of ticks fills most of the cell's
               horizontal span without overlap; SVG coords scale with viewport. -->
          {#each series as s (s.i)}
            {@const tickW2 = Math.max(4, (PLOT_W2 / expanded.n) * 0.7)}
            <rect
              x={x2(s.i, expanded.n) - tickW2 / 2}
              y={M2.top + PLOT_H2 + 28}
              width={tickW2} height="16"
              fill={s.passed ? 'var(--viz-success)' : 'var(--viz-error)'}
              fill-opacity="0.85"
              rx="1"
            >
              <title>trial {s.i}: {s.passed ? 'passed' : 'failed'} — running pass rate {(s.rate * 100).toFixed(1)}% [{(s.low * 100).toFixed(0)}%, {(s.high * 100).toFixed(0)}%]</title>
            </rect>
          {/each}

          <!-- Final point + label -->
          {#if series.length > 0}
            {@const last = series[series.length - 1]!}
            <circle
              cx={x2(last.i, expanded.n)} cy={y2(last.rate)}
              r="5" fill="var(--viz-accent)" stroke="var(--bg)" stroke-width="1.5"
            />
            <text
              x={x2(last.i, expanded.n) - 8}
              y={y2(last.rate) - 10}
              text-anchor="end"
              class="point-label"
            >{(last.rate * 100).toFixed(1)}%</text>
          {/if}
        </svg>
      </div>

      <Prose>
        <p>
          The pass rate is a measurement, not a hunch. It stabilizes under
          repetition and moves smoothly with difficulty. For a task like this,
          reliability behaves like a function, not a coin flip.
        </p>
      </Prose>
    {/if}

  {/if}
</section>

<style>
  .section {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-bottom: var(--space-4xl);
    width: 100%;
  }
  .header {
    margin-bottom: var(--space-lg);
  }
  .eyebrow {
    font-family: var(--font-sans);
    font-size: var(--text-xs);
    font-weight: var(--weight-medium);
    color: var(--ink-muted);
    text-transform: uppercase;
    letter-spacing: 0.08em;
    margin-bottom: var(--space-sm);
  }
  .summary {
    font-family: var(--font-serif);
    font-style: italic;
    font-size: var(--text-lg);
    color: var(--ink-muted);
    line-height: var(--leading-snug);
    margin-top: var(--space-sm);
    margin-bottom: var(--space-lg);
  }
  .subhead {
    font-family: var(--font-sans);
    font-size: var(--text-base);
    font-weight: var(--weight-medium);
    color: var(--ink);
    margin-top: var(--space-xl);
    margin-bottom: var(--space-sm);
  }
  .chart-wrap {
    width: 100%;
    max-width: 880px;
    padding: var(--space-md);
    margin: var(--space-lg) 0;
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
    background: var(--surface);
  }
  .chart-wrap svg {
    width: 100%;
    height: auto;
    display: block;
  }
  .chart-wrap :global(.axis-label) {
    font-family: var(--font-sans);
    font-size: 11.5px;
    fill: var(--ink-muted);
  }
  .chart-wrap :global(.axis-label.dim) {
    fill: var(--ink-subtle);
  }
  .chart-wrap :global(.axis-title) {
    font-family: var(--font-sans);
    font-size: 12px;
    fill: var(--ink-muted);
  }
  .chart-wrap :global(.point-label) {
    font-family: 'JetBrains Mono', ui-monospace, monospace;
    font-size: 11px;
    fill: var(--ink);
    font-weight: 500;
  }
  .chart-wrap :global(.target-label) {
    font-family: var(--font-sans);
    font-size: 10.5px;
    fill: var(--viz-accent);
  }
</style>
